import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SearchRemoveIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { Button, HiddenVisually, Icon, List, Text } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData, UISearchResultsBanner } from "@flights/types";
import useCreateUrl from "../../../hooks/useCreateUrl";
import useSearchFilters from "../../../hooks/useSearchFilters";
import { trackCustomGoal, trackExperimentStage } from "../../../utils/et";
import { useSelectorAppliedFilters } from "../../../store/selectors/appliedFilters";
import { getExpandedSearchAirports } from "../../../store/selectors/expandedSearch";
import { isSirfAvailable } from "../../../store/selectors/sirf";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../store";
import { Sidebar } from "../Container";
import FlightCardDesktop from "../FlightCard/FlightCard.desktop";
import SearchBaggagePolicies from "../SearchBaggagePolicies";
import SearchFiltersSummary from "../SearchFiltersSummary";
import SearchPagination from "../SearchPagination";
import SearchTabs from "../SearchTabs";
import SirfInfoBanner from "../SirfInfoBanner";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop";
import { DESKTOP_SIDEBAR_WIDTH, FILTERS } from "../../../constants";

import useClientCachedURL from "hooks/useClientCachedURL";
import useTrackExpRankerV3 from "hooks/useTrackExpRankerV3";
import { DestinationUkraineWarning } from "../TravelAlert";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner";
import { NoResultsFound } from "./components/NoResultsFound";
import RefreshResults from "./components/RefreshResults";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights";
import { TooManyFilters } from "./components/TooManyFilters";
import GeniusBanner from "cross-sell/components/GeniusBanner";
import { isOfMetaOrigin } from "utils/marketing-url-params";
import useGlobalContext from "hooks/useGlobalContext";
import useEventTracking from "hooks/useEventTracking";
import DSABanner from "../DSABanner/DSABanner";
import { getLocationParams } from "./utils";
import GeniusTCBanner from "cross-sell/components/GeniusTCBanner";
import Frame from "../Frame";
import { FlightCampaignTCBanner } from "../FlightCampaignTCBanner";
import { flightsSessionStore, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { FlexibleDateBestPriceDesktop } from "../FlexibleDateBestPrice";
import styles from "./SearchResults.desktop.module.css";
import { useFlexDatesRectrictedPOS } from "../FlexibleDateBestPrice/useFlexDatesRectrictedPOS";
import useProfiler from "hooks/useProfiler";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner";
import { RemoteAppleCardSR, WishlistWidgetLoader } from "components/remote";
import NoSSR from "../../../app/NoSSR";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import useOfferCartOrderData from "hooks/useOfferCartOrderData";
import useAlternativeFaresParams from "hooks/useAlternativeFaresParams";
import { useTrackGeniusSRGoals } from "utils/experiments/customer-aquisition/flights_web_cat_genius_campaign_www";
import { useSelector } from "react-redux";
import { getIndexCampaigns } from "store/indexCampaigns/selectors";
import flights_ace_web_esim_banner, {
  useTrackEsimBannerStages
} from "utils/experiments/ace/flights_ace_web_esim_banner";
import EsimBanner from "../../../ancillaries/components/Esim/EsimBanner";
import { getEsimEligibilitySearch } from "ancillaries/store/esim/selectors";
import useIsInViewport from "hooks/useIsInViewport";
import Wrapper from "components/atoms/Wrapper";
import { useEsimSearch } from "ancillaries/hooks/Esim/useEsimFetch";
import flights_web_sr_gen_ai_summary_v2_ddot_us from "utils/experiments/funnel/flights_web_sr_gen_ai_summary_v2_ddot_us";
import AiSummaryDesktop from "./components/AiSummary.desktop";
import useLocaleContext from "hooks/useLocaleContext";
import flights_web_hide_genius_banner from "utils/experiments/funnel/flights_web_hide_genius_banner";
import flights_web_reduce_spacing_sr_ddot from "utils/experiments/funnel/flights_web_reduce_spacing_sr_ddot";
import useTrackFlightsFlexiBannerSourceChange from "utils/experiments/funnel/flights_web_flexi_banner_source_change_ml";
import SearchFiltersDesktop from "../SearchFilters/SearchFilters.desktop";
import { DSATraceabilityPopover } from "../FlightDetailsInner/components/DSATraceability/DSATraceability.popover";
import { WhitelabelAncillaryBanner } from "ancillaries/components/Whitelabel/WhitelabelAncillaryBanner/WhitelabelAncillaryBanner";
import ddot_wl_save_flights from "utils/experiments/funnel/ddot_wl_save_flights";
import flights_web_filters_budget_per_adult_3 from "utils/experiments/funnel/flights_web_filters_budget_per_adult_3";
import dealsFilterExperiment from "utils/experiments/genius/flights_ade_sr_all_deals_filter";
import { getAvailableFilters } from "store/searchResults/selectors";
import flights_web_2_step_ranker from "utils/experiments/funnel/flights_web_2_step_ranker";
import flights_split_bounds_us, { useTrackSplitBoundsStages } from "utils/experiments/funnel/flights_split_bounds_us";
import flights_web_flexible_ticket_icon from "utils/experiments/funnel/flights_web_flexible_ticket_icon";

type SearchResultsListProps = {
  flights: UIFlightData[];
  onSelectFlight: (flight: UIFlightData) => void;
  hasAppliedFilters?: boolean;
};

const _SearchResultsList = ({ flights, onSelectFlight }: SearchResultsListProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const {
    search,
    searchCriteria,
    searchResults: { campaignBanner, fetchStatus, remoteBanners, displayOptions }
  } = store;
  const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
  const { isLoggedOut, isDirect, isDesktop, isWithKids, isPointOfSale, isOneWay, isRountrip, isFamily } = useProfiler();
  const params = new URLSearchParams(location.search);
  const currentSearchResultsPageNumber = params.get("page");

  /* start - flights_split_bounds_us */
  useTrackSplitBoundsStages();
  /* end - flights_split_bounds_us */

  // flights_web_flexi_banner_source_change_ml -- start
  const { trackStages } = useTrackFlightsFlexiBannerSourceChange();
  useEffect(() => {
    trackStages();
  }, [trackStages]);
  // flights_web_flexi_banner_source_change_ml -- end

  /** start - flights_ace_web_esim_banner */
  const esimBannerRef = useRef<HTMLDivElement>(null);
  const eSimInViewport = useIsInViewport(esimBannerRef.current);
  const isEsimEligible = !!useSelector(getEsimEligibilitySearch);
  const showEsimBanner = isEsimEligible && !!flights_ace_web_esim_banner.trackWithDefaultStage();
  const ESIM_BANNER_SEARCH_RESULTS_INDEX = 6;
  const WHITELABEL_BANNER_SEARCH_RESULTS_INDEX = 7;
  const fetchEsim = useEsimSearch();
  /** end - flights_ace_web_esim_banner */
  useTrackEsimBannerStages(isEsimEligible);
  /** start - flights_web_hide_genius_banner */
  const trackingDivRef = useRef<HTMLDivElement>(null);
  const isTrackingDivVisible = useIsInViewport(trackingDivRef.current);

  useEffect(() => {
    if (!isLoggedOut() && isTrackingDivVisible) {
      flights_web_hide_genius_banner.stages.main();
      isDirect() ? flights_web_hide_genius_banner.stages.direct() : flights_web_hide_genius_banner.stages.meta();
      isDesktop() ? flights_web_hide_genius_banner.stages.desktop() : flights_web_hide_genius_banner.stages.mobile();
    }
  }, [isTrackingDivVisible, isLoggedOut, isDirect, isDesktop]);
  /** end - flights_web_hide_genius_banner */

  const availableFilters = useSelector(getAvailableFilters);
  const appliedFilters = useSelectorAppliedFilters(store);
  isLoggedOut() ? null : dealsFilterExperiment.stages.users_in_sr();
  !!availableFilters.deals
    ? dealsFilterExperiment.useTrackStagesAllDealsFilter(
        appliedFilters.length > 0,
        appliedFilters.includes(FILTERS.allDeals)
      )
    : null;

  /* start - flights_ace_web_esim_banner */
  useEffect(() => {
    void fetchEsim();
  }, [fetchEsim]);

  useEffect(() => {
    if (eSimInViewport) {
      flights_ace_web_esim_banner.stages.viewed_esim_banner_sr_page();
      flights_ace_web_esim_banner.goals.viewed_esim_banner();
    }
  }, [eSimInViewport]);
  /* end - flights_web_ace_esim_banner */

  useEffect(() => {
    if (fetchStatus === "success" && flights.length > 0) {
      /* start - flights_web_reduce_spacing_sr_ddot */
      flights_web_reduce_spacing_sr_ddot.stages.main();
      isDirect()
        ? flights_web_reduce_spacing_sr_ddot.stages.direct()
        : flights_web_reduce_spacing_sr_ddot.stages.meta();
      isWithKids() && flights_web_reduce_spacing_sr_ddot.stages.isWithKids();
      isPointOfSale("us") && flights_web_reduce_spacing_sr_ddot.stages.usPointOfSale();
      isRountrip() && flights_web_reduce_spacing_sr_ddot.stages.roundTrip();
      isOneWay() && flights_web_reduce_spacing_sr_ddot.stages.oneWay();
      /* end - flights_web_reduce_spacing_sr_ddot */
    }
  }, [fetchStatus, flights.length, isDirect, isWithKids, isPointOfSale, isOneWay, isRountrip, isFamily]);

  //There isn't a page param for the first search
  const isFirstSearchResultsPage = currentSearchResultsPageNumber ? Number(currentSearchResultsPageNumber) === 1 : true;

  useEffect(() => {
    flights_apex_web_ancillaries_micro_conversion_aa.trackSearchResultsStages(flights);
  }, [flights]);

  useEffect(() => {
    const { adults, children } = searchCriteria;
    const travellersCount = adults + children.length;

    trackExperimentStage("flights_web_round_price_search_results_desktop", 1);
    trackExperimentStage("flights_web_round_price_search_results_desktop", isOfMetaOrigin() ? 3 : 2);
    trackExperimentStage("flights_web_round_price_search_results_desktop", 4);
    trackExperimentStage("flights_web_round_price_search_results_desktop", travellersCount > 1 ? 6 : 5);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldShowFlexDates =
    !displayOptions?.hideFlexiblePricesBanner &&
    flights.length >= 5 &&
    store.search.cabinClass === "ECONOMY" &&
    isFirstSearchResultsPage &&
    !isRestricedFlexDatePOS &&
    !isOfMetaOrigin();

  const showGeniusCampaignBanner =
    isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") && campaignBanner;

  const bannerBottomMargin =
    fetchStatus === "success" && flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? 2 : 4;

  useTrackGeniusSRGoals(!!useSelector(getIndexCampaigns).genius && !!showGeniusCampaignBanner);
  const renderGeniusBanner = () => {
    if (isLoggedOut()) {
      return null;
    }

    /** start - flights_web_hide_genius_banner */
    if (flights_web_hide_genius_banner.trackWithDefaultStage()) {
      return <div ref={trackingDivRef}></div>;
    }
    /** end - flights_web_hide_genius_banner */

    return (
      <Frame mt={2} mb={bannerBottomMargin}>
        <GeniusBanner />
        {/** start - flights_web_hide_genius_banner */}
        <div ref={trackingDivRef}></div>
        {/** end - flights_web_hide_genius_banner */}
        <SearchTrafficAAGeniusTrackerDesktop />
      </Frame>
    );
  };

  return (
    <div
      /* start - flights_web_reduce_spacing_sr_ddot */
      className={flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? styles.searchResultsList : undefined}
      /* end - flights_web_reduce_spacing_sr_ddot */
    >
      <List attributes={{ style: { padding: 0 } }}>
        {flights.map((flight: UIFlightData, index: number) => {
          return (
            <div key={`${index}_${flight.token}`}>
              {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              campaignBanner &&
              index === campaignBanner.position ? (
                <Frame mt={2} mb={bannerBottomMargin}>
                  <FlightCampaignTCBanner campaignBanner={campaignBanner} />
                </Frame>
              ) : null}
              {Array.isArray(remoteBanners) &&
              remoteBanners.includes("APPLE_CARD_BANNER_SR") &&
              index === flights.length - 1 ? (
                <RemoteAppleCardSR />
              ) : undefined}
              <div id={`flightcard-${index}`} data-testid="searchresults_card">
                <FlightCardDesktop
                  flight={flight}
                  onClick={onSelectFlight}
                  pricePerPerson
                  index={index}
                  searchType={search.type}
                />
              </div>
              {shouldShowFlexDates && index === 0 ? <FlexibleDateBestPriceDesktop /> : null}
              {!campaignBanner && index === 1 ? renderGeniusBanner() : null}
              {/* If GeniusCampaign is Enabled, show the Genius TC campaign on 3rd position */}
              {!isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              index === 1 &&
              campaignBanner ? (
                <Frame mt={2} mb={bannerBottomMargin}>
                  <GeniusTCBanner campaignBanner={campaignBanner} />
                  <SearchTrafficAAGeniusTrackerDesktop />
                </Frame>
              ) : null}
              {/**
               * If the position is larger than the number of cards displayed in the SR page,
               * then the T&C banner is shown after the last card.
               * More info: https://jira.booking.com/jira/browse/FPRICING-809
               */}
              {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              campaignBanner &&
              campaignBanner.position >= flights.length && // campaign banner position is larger than the number of cards
              index >= flights.length - 1 ? ( // after the last flight card
                <Frame mt={2} mb={bannerBottomMargin}>
                  <FlightCampaignTCBanner campaignBanner={campaignBanner} />
                </Frame>
              ) : null}
              {/* If GeniusCampaign is Enabled, show the ABU campaign on 6th position */}
              {campaignBanner && index === 4 ? renderGeniusBanner() : null}
              {/* start - flights_ace_web_esim_banner */}
              {showEsimBanner && index === ESIM_BANNER_SEARCH_RESULTS_INDEX ? (
                <Wrapper
                  elementRef={esimBannerRef}
                  noBorder
                  noPadding
                  noPaddingVertical
                  style={{
                    paddingBottom: flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? "0px" : "8px",
                    paddingBlockStart: flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? "8px" : "0px"
                  }}
                >
                  <EsimBanner context="search" />
                </Wrapper>
              ) : null}
              {/* end - flights_ace_web_esim_banner */}

              {index === WHITELABEL_BANNER_SEARCH_RESULTS_INDEX ? (
                <WhitelabelAncillaryBanner ancillary="loungeAccess" />
              ) : null}
            </div>
          );
        })}
      </List>
    </div>
  );
};

const SearchResultsList = React.memo(_SearchResultsList);

// Temp fix for carousel increasing width
const attributes = { style: { width: `calc(100% - ${DESKTOP_SIDEBAR_WIDTH}px)` } };

const SearchResultsWithSidebarAndTabs: FC<{
  children: React.ReactNode;
  humanRightBanner?: UISearchResultsBanner;
  shouldMoveBudgetFilter?: boolean;
}> = ({ children, humanRightBanner, shouldMoveBudgetFilter }) => {
  const i18n = useI18n();
  const { locale } = useLocaleContext();
  const { isAmerican } = useProfiler();

  const isEligibleForAiSummaryUs = isAmerican() && locale?.code.toLowerCase().startsWith("en-");
  const isEligibleForAiSummaryNonUs = !isAmerican() && locale?.code.toLowerCase().startsWith("en-");

  flights_web_sr_gen_ai_summary_v2_ddot_us.useTrackStages(isEligibleForAiSummaryUs);

  return (
    <div className={styles.searchResults} data-testid="searchresults_list">
      <NoSSR>
        <Frame direction="row">
          <Sidebar fixScrollPadding pt={flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? 0 : 4}>
            {Boolean(
              isEligibleForAiSummaryNonUs ||
                (isEligibleForAiSummaryUs && flights_web_sr_gen_ai_summary_v2_ddot_us.trackWithDefaultStage())
            ) ? (
              <AiSummaryDesktop />
            ) : null}

            <Frame pb={3}>
              <SearchFiltersSummary />
            </Frame>
            <SearchFiltersDesktop shouldMoveBudgetFilter={shouldMoveBudgetFilter} />
            <DSATraceabilityPopover />
          </Sidebar>

          <Frame
            direction="column"
            grow={1}
            pt={flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage() ? 0 : 4}
            pb={4}
            pl={6}
            attributes={attributes}
          >
            <HiddenVisually>
              <Text tagName="h2">{i18n.trans(t("a11y_flights_sr__results_hidden_heading"))}</Text>
            </HiddenVisually>
            <SearchResultBanner banner={humanRightBanner} />
            <DSABanner />
            <SearchResultsTabsContainer>{children}</SearchResultsTabsContainer>
          </Frame>
        </Frame>
      </NoSSR>
    </div>
  );
};
const SearchResultsTabsContainer = (props: PropsWithChildren<{}>) => (
  <SearchTabs showBestRankingInfo={true}>{props.children}</SearchTabs>
);

type FailedSearchResultsProps = {
  expandToCitySearch: () => void;
  i18n: I18nChildContext;
};

const FailedSearchResults = ({ expandToCitySearch, i18n }: FailedSearchResultsProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { search } = store;
  return (
    <Frame mt={6} mb={6} attributes={{ style: { minHeight: "40vh" } }}>
      <Frame m={3} alignItems="center">
        <Icon svg={SearchRemoveIcon} size="small" className={styles.searchRemoveIcon} />
        <Text variant="headline_2">{i18n.trans(t("flights_search_no_results_header"))}</Text>
      </Frame>
      <Frame alignItems="center">
        <Text variant="body_2">{i18n.trans(t("flights_search_no_results_copy_desktop"))} </Text>
      </Frame>
      {search.type !== "MULTISTOP" && (
        <Frame alignItems="center" m={6}>
          <Button onClick={expandToCitySearch}>{i18n.trans(t("flights_search_no_results_nearbyairports_cta"))}</Button>
        </Frame>
      )}
    </Frame>
  );
};

interface SearchResultsDesktopSectionProps {
  airports: string;
  onSelectFlight: () => void;
  hasAppliedFilters?: boolean;
}

const SearchResultsDesktopSection = ({
  airports,
  onSelectFlight,
  hasAppliedFilters
}: SearchResultsDesktopSectionProps) => {
  const history = useHistory();
  const location = useLocation();
  const { createUrl } = useCreateUrl();
  const { applyFilters } = useSearchFilters();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { search, searchResults } = store;
  // fetchStatus needed for flights_web_2_step_ranker
  const { flights = [], subsidizedFaresSummary, fetchStatus } = searchResults;
  const i18n = useI18n() as I18nChildContext;
  const { cacheLastSuccessfulSearchUrl } = useClientCachedURL();
  const trackExpRankerV3 = useTrackExpRankerV3();
  const offerCartOrderData = useOfferCartOrderData();
  const { setAlternativeFaresParams } = useAlternativeFaresParams();
  const { isLoggedOut } = useProfiler();

  useEffect(() => {
    applyFilters(search.filtersDraft);
  }, [search.filtersDraft, applyFilters]);

  /* start - flights_web_2_step_ranker */
  flights_web_2_step_ranker.useTrackStages(fetchStatus === "success" && flights.length > 0);
  const isInTopFiveOffers = useCallback(
    (flight: UIFlightData) => {
      return flights
        .slice(0, 5)
        .map((flight) => flight.token)
        .includes(flight.token);
    },
    [flights]
  );
  /* end - flights_web_2_step_ranker */

  const showFlightDetails = useCallback(
    async (flight: any) => {
      onSelectFlight();

      /* start - flights_web_2_step_ranker */
      const isInTopFive = isInTopFiveOffers(flight);
      if (isInTopFive) {
        flights_web_2_step_ranker.trackClickedTopFive();
        offerCartOrderData.set(flight.token, {
          isInTopFiveOffers: true
        });
      }
      /* end - flights_web_2_step_ranker */

      flights_split_bounds_us.goals.cta_click();
      trackCustomGoal("flights_meta_land_on_cheapest_flights", 4); //select flight
      offerCartOrderData.set(flight.token, {
        offer: flight.token,
        cabinClass: searchResults.searchCriteria?.cabinClass
      });

      const url = createUrl(`/flights/${airports}/${flight.token}/?${getLocationParams(location.search)}`);
      history.push(url);
    },
    [
      onSelectFlight,
      offerCartOrderData,
      searchResults.searchCriteria?.cabinClass,
      createUrl,
      airports,
      location.search,
      history,
      isInTopFiveOffers
    ]
  );

  cacheLastSuccessfulSearchUrl(airports);
  searchResults.fetchedAt && setAlternativeFaresParams("searchedAt", searchResults.fetchedAt);
  trackExpRankerV3.trackStages();
  return (
    <>
      <NoDirectFlightsBanner />
      <DestinationUkraineWarning />
      {isSirfAvailable(subsidizedFaresSummary) && (
        <SirfInfoBanner
          bannerName="search-results"
          bannerContent={i18n.trans(t("flights_sr_spanish_discount_banner"))}
        />
      )}
      <SearchBaggagePolicies />
      <SearchResultsList flights={flights} onSelectFlight={showFlightDetails} hasAppliedFilters={hasAppliedFilters} />
      <SearchPagination />
      <RefreshResults flights={flights} />
      {!isLoggedOut() && ddot_wl_save_flights.trackWithDefaultStage() ? <WishlistWidgetLoader /> : null}
    </>
  );
};
type ComposedSearchResultsProps = SearchResultsDesktopSectionProps;

const ComposedSearchResults = ({ airports, onSelectFlight }: ComposedSearchResultsProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const { createUrl } = useCreateUrl();
  const { applyFilters } = useSearchFilters();
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("search_results", requestId);
  const {
    search,
    searchResults: { flights = [], fetchStatus, error, availableFilters, banners },
    flexibleDateMinPrice: { fetchStatus: flexibleDatePriceFetchStatus }
  } = store;
  const appliedFilters = useSelectorAppliedFilters(store);
  const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
  const humanRightBanner = banners?.find((banner: UISearchResultsBanner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
  useEffect(() => {
    applyFilters(search.filtersDraft);
  }, [search.filtersDraft, applyFilters]);

  const i18n = useI18n() as I18nChildContext;

  const expandedSearchAirports = useMemo(() => {
    const { searchSegments } = search;
    /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
    /**
     * TODO: Update ExpandedSeach for multistop cases?
     */
    const { from, to } = searchSegments[0];
    return getExpandedSearchAirports(from, to);
  }, [search]);

  const expandToCitySearch = useCallback(() => {
    if (expandedSearchAirports) {
      const { fromCities, toCities, fromLocationName, toLocationName } = expandedSearchAirports;
      const params = new URLSearchParams(location.search);
      params.set("from", fromCities);
      params.set("to", toCities);
      params.set("fromLocationName", fromLocationName);
      params.set("toLocationName", toLocationName);

      history.replace(createUrl(`/flights/${fromCities}-${toCities}/?${params}`));
      window.location.reload();
    }
  }, [history, location, expandedSearchAirports, createUrl]);

  /* start - flights_web_filters_budget_per_adult_3 */
  const { isSolo } = useProfiler();
  const [shouldMoveBudgetFilter, setShouldMoveBudgetFilter] = useState(isSolo());
  /* end - flights_web_filters_budget_per_adult_3 */

  useEffect(() => {
    /* start - flights_web_filters_budget_per_adult_3 */
    // We need the filters to only rerender/update when search is submitted,
    // therefore we only update the "relevant" solo state when results are being loaded
    if (fetchStatus === "loading") {
      setShouldMoveBudgetFilter(isSolo() && !!flights_web_filters_budget_per_adult_3.trackWithDefaultStage());
    }
    /* end - flights_web_filters_budget_per_adult_3 */

    if (["loading", "initial"].includes(fetchStatus)) return;
    if (flights.length === 0) trackV2("show_empty_search_results");
  }, [flights, fetchStatus, trackV2, isSolo]);

  useEffect(() => {
    if (fetchStatus === "success" && flights.length > 0) {
      flights_web_flexible_ticket_icon.trackWithDefaultStage();
    }
  }, [flights, fetchStatus, trackV2]);

  const firstLoad = !availableFilters.durationMin;
  // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
  const noFlightsFound = (error && error["code"] === "SEARCH_SEARCHFLIGHTS_NO_FLIGHTS_FOUND") || flights.length === 0;
  const canExtendSearch = appliedFilters.length === 0 && noFlightsFound && expandedSearchAirports;

  const canRemoveDirectFlightsFilter = appliedFilters.includes(FILTERS.stops) && noFlightsFound;
  let resultingComp = undefined;

  const flexibleFetchConditionalStatus = useMemo(() => {
    if (store.search.cabinClass === "ECONOMY" && !isRestricedFlexDatePOS && !isOfMetaOrigin()) {
      return {
        loading: flexibleDatePriceFetchStatus === "initial" || flexibleDatePriceFetchStatus === "loading",
        success: flexibleDatePriceFetchStatus === "success"
      };
    }

    return {
      loading: false,
      success: true
    };
  }, [flexibleDatePriceFetchStatus, isRestricedFlexDatePOS, store.search.cabinClass]);

  if (fetchStatus === "initial" || fetchStatus === "loading" || flexibleFetchConditionalStatus.loading) {
    if (firstLoad) {
      // this is the result of SSR
      return <SkeletonSearchDesktop />;
    }
    // need sidebar from now on and no SSR
    resultingComp = <SkeletonSearchContent />;
  }

  if (fetchStatus === "failed" && canExtendSearch) {
    // this can also be SSR
    return <FailedSearchResults expandToCitySearch={expandToCitySearch} i18n={i18n} />;
  }

  // case of success should go here, i.e. the composed result
  if (fetchStatus === "success") {
    if (flights.length > 0) {
      // No SSR for this case, why not ? because api is called from client side
      resultingComp = (
        <SearchResultsDesktopSection
          airports={airports}
          onSelectFlight={onSelectFlight}
          hasAppliedFilters={appliedFilters.length > 0}
        />
      );
    } else if (canRemoveDirectFlightsFilter) {
      // can be generated from SSR
      return (
        <>
          <SearchResultBanner banner={humanRightBanner} />
          <RemoveDirectFlights />
        </>
      );
    }
  }

  if (!resultingComp) {
    // this is a fallthrough default when everything else fails
    if (appliedFilters.length > 0) {
      // need sidebar here too
      resultingComp = <TooManyFilters />;
    } else {
      return <NoResultsFound />;
    }
  }

  const screenReaderText = () => {
    if (appliedFilters.length > 0 && flights) {
      return i18n.trans(t("a11y_flights_sr_results_with_filter"));
    }

    if (appliedFilters.length === 0 && flights) {
      return i18n.trans(t("a11y_flights_sr_results_loaded"));
    }

    return "";
  };

  return (
    <SearchResultsWithSidebarAndTabs
      humanRightBanner={humanRightBanner}
      shouldMoveBudgetFilter={shouldMoveBudgetFilter}
    >
      {resultingComp}

      <HiddenVisually>
        <div aria-live="polite">{screenReaderText()}</div>
      </HiddenVisually>
    </SearchResultsWithSidebarAndTabs>
  );
};

export const SearchTrafficAAGeniusTrackerDesktop: FC = () => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();

  useEffect(() => {
    if (!store.user.userInfo) {
      flightsSessionStore.set("genius_banner_seen", true);
    }
  }, [store.user.userInfo]);

  return null;
};

export default React.memo(ComposedSearchResults);
